import React from 'react';
import { Box, Typography } from '@mui/material';
import IPhoneFrame, {
  StatusBar, NavBar, TabBar, HomeBar,
  IOS_FONT, IOS_BLUE, IOS_GRAY_BG, IOS_GRAY_FIELD, IOS_GRAY_TEXT, IOS_SEPARATOR,
} from './IPhoneFrame';
import { APP_LOGOS, APP_COLORS, APP_NAMES } from '../sharedMockData';

const APP_TILES = [
  { key: 'khan',       logo: APP_LOGOS.khan,       color: APP_COLORS.khan,       name: APP_NAMES.khan,       status: 'active',  subtitle: 'Algebra 1 · 12 mastered' },
  { key: 'icivics',    logo: APP_LOGOS.icivics,    color: APP_COLORS.icivics,    name: APP_NAMES.icivics,    status: 'active',  subtitle: 'Class 5A · 3 wins' },
  { key: 'readtheory', logo: APP_LOGOS.readtheory, color: APP_COLORS.readtheory, name: APP_NAMES.readtheory, status: 'active',  subtitle: 'Level 7 · Lexile 1020' },
  { key: 'learnfree',  logo: APP_LOGOS.learnfree,  color: APP_COLORS.learnfree,  name: APP_NAMES.learnfree,  status: 'active',  subtitle: 'Word course' },
  { key: 'w3schools',  logo: APP_LOGOS.w3schools,  color: APP_COLORS.w3schools,  name: APP_NAMES.w3schools,  status: 'active',  subtitle: 'HTML tutorial' },
  { key: 'careersafe', logo: APP_LOGOS.careersafe, color: APP_COLORS.careersafe, name: APP_NAMES.careersafe, status: 'pending', subtitle: 'Assignment pending' },
  { key: 'typingclub', logo: APP_LOGOS.typingclub, color: APP_COLORS.typingclub, name: APP_NAMES.typingclub, status: 'active',  subtitle: '42 WPM' },
  { key: 'duolingo',   logo: APP_LOGOS.duolingo,   color: APP_COLORS.duolingo,   name: APP_NAMES.duolingo,   status: 'active',  subtitle: '7 day streak' },
];

const statusChip = (s) => {
  if (s === 'active')  return { label: 'Active',  bg: '#E8F5E9', color: '#2E7D32' };
  if (s === 'pending') return { label: 'Pending', bg: '#FFF4E5', color: '#E65100' };
  return { label: 'Inactive', bg: '#ECEFF1', color: '#546E7A' };
};

const Tile = ({ app }) => {
  const chip = statusChip(app.status);
  return (
    <Box sx={{
      bgcolor: '#fff',
      borderRadius: '18px',
      p: 1.5,
      border: `0.5px solid ${IOS_SEPARATOR}`,
      display: 'flex', flexDirection: 'column', gap: 1,
      minHeight: 130,
      cursor: 'pointer',
      boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    }}>
      <Box sx={{
        width: 48, height: 48, borderRadius: 2.5,
        bgcolor: '#fff',
        border: `0.5px solid ${IOS_SEPARATOR}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden',
      }}>
        <Box
          component="img"
          src={app.logo}
          alt={app.name}
          sx={{ width: 42, height: 42, objectFit: 'contain' }}
          onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.style.background = app.color; }}
        />
      </Box>
      <Typography sx={{
        fontFamily: IOS_FONT, fontSize: '14px', fontWeight: 600, color: '#000',
        lineHeight: 1.15,
      }}>{app.name}</Typography>
      <Typography sx={{
        fontFamily: IOS_FONT, fontSize: '11px', color: IOS_GRAY_TEXT,
        lineHeight: 1.2,
      }}>{app.subtitle}</Typography>
      <Box sx={{ flex: 1 }} />
      <Box sx={{
        alignSelf: 'flex-start',
        bgcolor: chip.bg,
        color: chip.color,
        fontFamily: IOS_FONT, fontSize: '10px', fontWeight: 600,
        px: 1, py: 0.2, borderRadius: 10,
      }}>{chip.label}</Box>
    </Box>
  );
};

const IosDashboardMockup = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, bgcolor: '#eceff1', minHeight: '100%' }}>
    <IPhoneFrame
      title="2. Dashboard (Tile grid + iOS search)"
      caption="Post-login tile grid of integrated apps. iOS-style search bar, status chips, pull-to-refresh indicator visible."
    >
      <StatusBar />
      <NavBar
        title="My Apps"
        largeTitle
        trailingButton={{ label: '⊕', bold: true }}
      />
      {/* Pull to refresh indicator */}
      <Box sx={{
        position: 'absolute',
        top: 44 + 100,
        left: 0, right: 0, bottom: 83,
        overflow: 'auto',
      }}>
        <Box sx={{ px: 2, pt: 1 }}>
          {/* iOS search bar */}
          <Box sx={{
            bgcolor: IOS_GRAY_FIELD,
            borderRadius: 2.5,
            px: 1.5, py: 1,
            display: 'flex', alignItems: 'center', gap: 1,
            mb: 2,
          }}>
            <Box sx={{ fontSize: '15px', color: IOS_GRAY_TEXT }}>🔍</Box>
            <Typography sx={{
              fontFamily: IOS_FONT, fontSize: '16px', color: IOS_GRAY_TEXT, flex: 1,
            }}>Search apps</Typography>
            <Box sx={{ fontSize: '14px', color: IOS_GRAY_TEXT }}>🎙</Box>
          </Box>

          {/* Pull-to-refresh spinner hint */}
          <Box sx={{
            textAlign: 'center', color: IOS_GRAY_TEXT, mb: 1,
            fontFamily: IOS_FONT, fontSize: '12px',
          }}>
            ↓ Pull to refresh · last sync 2 min ago
          </Box>

          {/* Continue section */}
          <Typography sx={{
            fontFamily: IOS_FONT, fontSize: '13px', color: IOS_GRAY_TEXT,
            textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600,
            mb: 1, mt: 1,
          }}>Continue Where You Left Off</Typography>

          <Box sx={{
            bgcolor: '#fff', borderRadius: '14px', p: 1.5, mb: 2.5,
            border: `0.5px solid ${IOS_SEPARATOR}`,
            display: 'flex', alignItems: 'center', gap: 1.5,
          }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: 2,
              bgcolor: '#fff', border: `0.5px solid ${IOS_SEPARATOR}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden',
            }}>
              <Box component="img" src={APP_LOGOS.khan} alt="Khan" sx={{ width: 38, height: 38, objectFit: 'contain' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontFamily: IOS_FONT, fontSize: '15px', fontWeight: 600 }}>Khan Academy</Typography>
              <Typography sx={{ fontFamily: IOS_FONT, fontSize: '12px', color: IOS_GRAY_TEXT }}>
                Algebra 1 · Unit 4, Lesson 3
              </Typography>
            </Box>
            <Box sx={{ color: IOS_BLUE, fontSize: '18px', fontWeight: 300 }}>›</Box>
          </Box>

          {/* All apps section */}
          <Typography sx={{
            fontFamily: IOS_FONT, fontSize: '13px', color: IOS_GRAY_TEXT,
            textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600,
            mb: 1,
          }}>All Apps · 8 Available</Typography>

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 1.2,
          }}>
            {APP_TILES.map(a => <Tile key={a.key} app={a} />)}
          </Box>

          <Box sx={{ height: 20 }} />
        </Box>
      </Box>
      <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
        <TabBar activeIndex={0} />
      </Box>
      <HomeBar />
    </IPhoneFrame>
  </Box>
);

export default IosDashboardMockup;
